import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { NgxMatDatetimePicker } from '@angular-material-components/datetime-picker';
import { FormControl, FormGroup } from '@angular/forms';
import { FieldDefinition, FieldType } from '../../models/field-definition';
import { toDateValue } from './date-value';
import { buildValidators, resolveErrorMessage } from './validators.factory';

@Component({
  selector: 'app-dynamic-form',
  templateUrl: './dynamic-form.component.html',
  styleUrls: ['./dynamic-form.component.css']
})
export class DynamicFormComponent implements OnChanges {
  @Input() fields: FieldDefinition[] = [];
  @Input() value: Record<string, unknown> | null = null;
  @Output() formReady = new EventEmitter<FormGroup>();

  readonly fieldType = FieldType;
  formGroup: FormGroup = new FormGroup({});

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fields']) {
      this.buildForm();
    } else if (changes['value'] && this.value) {
      this.formGroup.patchValue(this.coerceValues(this.value), { emitEvent: false });
    }
  }

  private buildForm(): void {
    const group: Record<string, FormControl> = {};

    for (const field of this.fields ?? []) {
      const raw = this.value?.[field.key] ?? field.defaultValue ?? this.emptyValue(field);
      const initial = this.isDateBased(field) ? toDateValue(raw) : raw;
      // A disabled control keeps its value out of the submitted payload; readOnly is
      // handled in the template so the value still submits but cannot be edited.
      group[field.key] = new FormControl(
        { value: initial, disabled: !!field.disabled },
        buildValidators(field)
      );
    }

    this.formGroup = new FormGroup(group);
    this.formReady.emit(this.formGroup);
  }

  private emptyValue(field: FieldDefinition): unknown {
    if (field.type === FieldType.Checkbox) {
      return false;
    }
    // Text starts as '' (not null) so downstream .trim() calls on bound fields are safe.
    return field.type === FieldType.Text ? '' : null;
  }

  /** Both date and datetime fields hold a Date object; only their rendered inputs differ. */
  private isDateBased(field: FieldDefinition): boolean {
    return field.type === FieldType.Date || field.type === FieldType.DateTime;
  }

  /** Date controls only accept a Date, so incoming patches are coerced the same way as initial values. */
  private coerceValues(value: Record<string, unknown>): Record<string, unknown> {
    const dateKeys = (this.fields ?? []).filter(f => this.isDateBased(f)).map(f => f.key);
    if (!dateKeys.length) {
      return value;
    }
    const coerced = { ...value };
    for (const key of dateKeys) {
      if (key in coerced) {
        coerced[key] = toDateValue(coerced[key]);
      }
    }
    return coerced;
  }

  // A read-only or disabled date field keeps its value but must not open the calendar. The
  // control's own disabled state is checked too: the toggle and picker are bound explicitly
  // here, so they no longer inherit it from the input.
  isDateFieldLocked(field: FieldDefinition): boolean {
    return !!field.readOnly || !!field.disabled || !!this.formGroup.get(field.key)?.disabled;
  }

  // The input is readonly (no typing), so clicking it opens the calendar — otherwise the
  // only way in would be the toggle icon. Both the date and datetime fields use the same
  // picker component; only hideTime differs.
  openPicker(picker: NgxMatDatetimePicker<Date>, field: FieldDefinition): void {
    if (!this.isDateFieldLocked(field)) {
      picker.open();
    }
  }

  errorFor(field: FieldDefinition): string {
    const control = this.formGroup.get(field.key);
    if (!control || !control.touched || control.valid) {
      return '';
    }
    return resolveErrorMessage(field, control.errors);
  }
}
